import {
  AgendaItemPreEventCheck,
  BroadcastCheck,
  BroadcastVideoAssetPreEventCheck,
  EmailTemplatePreEventCheck,
  FailedChecks,
  PreEventTrack,
  UserPreEvent,
  VideoAssetErrorAndWarningCheck,
  FailedSeriesChecks,
  SeriesEmailTemplateCheck,
  BroadcastAtleastOneUnProcessedVideoCheck,
  EventResponse,
  SlideAssetWithBroadcast
} from '@goldcast/api/backend';

import { FormattedAlertForEntityItems } from './store/types';
import { FormattedAlert, FormattedAlertForAgendaItems } from './types';
import { PermissionType } from '../settings/Permissions/store/types';

import router from '@/router';
import store from '@/store';

function isUserHavePermission(permissionType: PermissionType[]): boolean {
  if (permissionType.length === 0) {
    return true;
  }

  return store.getters['studioPermissions/getIsPermissionAllowed']({
    permissionType: permissionType
  });
}

function navigateTo(
  routeName: string,
  params: { query?: { [key: string]: string }; hash?: string; params?: { [key: string]: string } } = {}
) {
  router.push({ name: routeName, query: params.query, hash: params.hash, params: params.params }).catch(() => {
    // Ignore
  });
}

export function formatFailedChecks(failedChecks: FailedChecks | {} = {}, event?: EventResponse): FormattedAlert[] {
  const formattedAlerts: FormattedAlert[] = [];
  const isWebinar = event?.event_type === 'Webinar';
  const isRepeatingEvent = event?.is_blueprint;
  const eventId = isRepeatingEvent ? event.series.id : event?.id || '';
  const isEventRoleEnabled = store.state.org.currentOrg?.is_event_roles_enabled;
  const showStaffAlerts = isEventRoleEnabled && event?.host_type !== 'In_Person';

  Object.keys(failedChecks).forEach(key => {
    const failedCheck = failedChecks[key];

    switch (key as keyof FailedChecks) {
      case 'agenda_items_not_in_any_track':
        (failedCheck as AgendaItemPreEventCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `"${c.agenda_item_title}" is not added to a track`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', {
                      query: { agendaItemId: String(c.agenda_item_id) }
                    });
                  } else {
                    navigateTo('Agenda', {
                      query: { agendaItemId: String(c.agenda_item_id) }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'broadcast_has_no_speaker':
        (failedCheck as BroadcastCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Session "${c.broadcast_title}" has no speakers`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'broadcasts_not_added_to_any_track':
        (failedCheck as BroadcastCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Session "${c.broadcast_title}" is not added to a track`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'staff_not_completed_tech_check':
        if (showStaffAlerts) {
          (failedCheck as UserPreEvent[]).forEach(c => {
            formattedAlerts.push({
              key,
              message: `Staff "${c.user_first_name} ${c.user_last_name}" has not completed the tech check`,
              actionRequired: !!c.action_required,
              action: () => {
                if (isRepeatingEvent) {
                  navigateTo('RepeatingEventCheckInStaff');
                } else if (isWebinar) {
                  navigateTo('WebinarStaff', { params: { eventId } });
                } else {
                  navigateTo('Staff');
                }
              }
            });
          });
        }
        break;
      case 'staffs_not_added_to_any_broadcasts':
        if (showStaffAlerts) {
          (failedCheck as UserPreEvent[]).forEach(c => {
            formattedAlerts.push({
              key,
              message: `Staff "${c.user_first_name} ${c.user_last_name}" is not added to any broadcasts`,
              actionRequired: !!c.action_required,
              action: () => {
                if (isRepeatingEvent) {
                  navigateTo('RepeatingEventCheckInStaff');
                } else if (isWebinar) {
                  navigateTo('WebinarStaff', { params: { eventId } });
                } else {
                  navigateTo('Staff');
                }
              }
            });
          });
        }
        break;
      case 'buffer_video':
        formattedAlerts.push({
          key,
          message: `Buffer video "${
            (failedCheck as VideoAssetErrorAndWarningCheck).video_name
          }" has an error or a warning`,
          actionRequired: !!failedCheck.action_required,
          action: isUserHavePermission(['look_and_feel:update'])
            ? () => {
                if (isRepeatingEvent) {
                  navigateTo('RepeatingEventLookAndFeel');
                } else if (isWebinar) {
                  navigateTo('WebinarLookAndFeel', { params: { eventId } });
                } else {
                  navigateTo('LookAndFeel', { hash: '#bufferVideo' });
                }
              }
            : null
        });
        break;
      case 'email_template_missing_magic_links':
        (failedCheck as EmailTemplatePreEventCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Email template "${c.name}" does not include users' magic links`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['email_template:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventEmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  } else if (isWebinar) {
                    navigateTo('WebinarEmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  } else {
                    navigateTo('EmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'email_template_missing_qrcode_or_location':
        (failedCheck as EmailTemplatePreEventCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Email template "${c.name}" does not include QR code or location`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['email_template:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventEmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  } else if (isWebinar) {
                    navigateTo('WebinarEmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  } else {
                    navigateTo('EmailTemplates', {
                      query: { emailTemplateId: c.event_email_template || '' }
                    });
                  }
                }
              : null,
            entityId: c.event_email_template
          });
        });
        break;
      case 'registration_confirmation_email_turned_off':
        formattedAlerts.push({
          key,
          message: 'Registration confirmation emails will not be sent',
          actionRequired: !!failedCheck.action_required,
          action: isUserHavePermission(['email:send'])
            ? () => {
                if (isRepeatingEvent) {
                  navigateTo('RepeatingEventEmailSchedule', { hash: '#schedule-drawer' });
                } else if (isWebinar) {
                  navigateTo('WebinarEmailSchedule', { hash: '#schedule-drawer' });
                } else {
                  navigateTo('EmailSchedule', { hash: '#schedule-drawer' });
                }
              }
            : null
        });
        break;
      case 'simulive_broadcast_have_improper_schedule':
        (failedCheck as BroadcastCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `${c.broadcast_title}'s agenda needs attention`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'simulive_broadcast_with_improper_video_length':
        (failedCheck as BroadcastVideoAssetPreEventCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message:
              {
                TOO_LONG: `Goldcast recommends your session "${c.broadcast_title}" be 2 minutes longer than your video length, to ensure the video plays fully. Your video may be cut off at the end of your session time.`,
                TOO_SHORT: `Total video length is over 2 minutes shorter than the session duration for your session "${c.broadcast_title}"`
              }[c.error_type] ||
              `Total video length exception for your session "${c.broadcast_title} - ${c.error_type}"`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'simulive_broadcast_with_no_video':
        (failedCheck as BroadcastCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `"${c.broadcast_title}" has no video`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'speaker_not_completed_tech_check':
        (failedCheck as UserPreEvent[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Speaker "${c.user_first_name} ${c.user_last_name}" has not completed the tech check`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingSpeakers');
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Speakers', {});
                  }
                }
              : null
          });
        });
        break;
      case 'speakers_not_added_to_any_broadcasts':
        (failedCheck as UserPreEvent[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Speaker "${c.user_first_name} ${c.user_last_name}" is not added to any broadcasts`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingSpeakers');
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Speakers');
                  }
                }
              : null
          });
        });
        break;
      case 'track_has_overlapping_sessions':
        (failedCheck as PreEventTrack[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `${
              store.state.studioAgenda?.agendaTracks?.length === 1 ? 'Event' : `Track "${c.name}"`
            } has overlapping sessions. Attendees may be redirected at the start of the later session and miss the end of the first session.`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => navigateTo(isRepeatingEvent ? 'RepeatingEventAgenda' : 'Agenda', { query: { trackId: c.id } })
              : null
          });
        });
        break;
      case 'upload_video_errors':
        (failedCheck as VideoAssetErrorAndWarningCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Video "${c.video_name}" has an error`,
            actionRequired: !!c.action_required,
            action: null
          });
        });
        break;
      case 'upload_video_warnings':
        (failedCheck as VideoAssetErrorAndWarningCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Video "${c.video_name}" has a warning`,
            actionRequired: !!c.action_required,
            action: null
          });
        });
        break;
      case 'upload_video_still_processing':
        (failedCheck as VideoAssetErrorAndWarningCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `This video "${c.video_name}" will not play in the session until it is fully processed.`,
            actionRequired: !!c.action_required,
            action: null
          });
        });
        break;
      case 'simulive_broadcast_with_no_processed_video':
        (failedCheck as BroadcastCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `Session "${c.broadcast_title}" has no processed videos. Videos must be processed to play during the session.`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      case 'broadcast_with_atleast_one_unprocessed_video':
        (failedCheck as BroadcastAtleastOneUnProcessedVideoCheck[]).forEach(c => {
          (c.videos as unknown as VideoAssetErrorAndWarningCheck[]).forEach(unprocessedVideo => {
            formattedAlerts.push({
              key,
              message: `Session ${c.broadcast_title}'s video "${
                (unprocessedVideo as unknown as VideoAssetErrorAndWarningCheck).video_name
              }" is processing. The video will not play until it's fully processed.`,
              actionRequired: !!c.action_required,
              action: isUserHavePermission(['event:update'])
                ? () => {
                    if (isRepeatingEvent) {
                      navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                    } else if (isWebinar) {
                      navigateTo('WebinarDetails', { params: { eventId } });
                    } else {
                      navigateTo('Agenda', {
                        query: { broadcastId: c.broadcast_id }
                      });
                    }
                  }
                : null
            });
          });
        });
        break;
      case 'broadcast_with_atleast_one_errored_video':
        (failedCheck as BroadcastAtleastOneUnProcessedVideoCheck[]).forEach(c => {
          (c.videos as unknown as VideoAssetErrorAndWarningCheck[]).forEach(unprocessedVideo => {
            formattedAlerts.push({
              key,
              message: `Session ${c.broadcast_title}'s video "${
                (unprocessedVideo as unknown as VideoAssetErrorAndWarningCheck).video_name
              }" has a processing error. Please re-upload.`,
              actionRequired: !!c.action_required,
              action: isUserHavePermission(['event:update'])
                ? () => {
                    if (isRepeatingEvent) {
                      navigateTo('RepeatingEventAgenda', { query: { broadcastId: c.broadcast_id } });
                    } else if (isWebinar) {
                      navigateTo('WebinarDetails', { params: { eventId } });
                    } else {
                      navigateTo('Agenda', {
                        query: { broadcastId: c.broadcast_id }
                      });
                    }
                  }
                : null
            });
          });
        });
        break;
      case 'upload_slide_errors':
        (failedCheck as SlideAssetWithBroadcast[]).forEach(c => {
          const hasMp4Error = c.transcoder_errors?.includes('SLIDE_CONTAINS_MP4');
          const slideConversionFailed = c.transcoder_errors?.includes('SLIDE_CONVERSION_FAILED');
          if (!hasMp4Error && !slideConversionFailed) {
            return;
          }
          formattedAlerts.push({
            key,
            message: hasMp4Error
              ? `Animated slides aren't supported for decks that contain embedded video. Please remove any video from your PowerPoint and upload it again.`
              : `Animations couldn't be processed for this deck. Slides can still be shared, but without animations.`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () => {
                  if (isRepeatingEvent) {
                    navigateTo('RepeatingEventAgenda', { query: { broadcastId: c?.broadcast_id } });
                  } else if (isWebinar) {
                    navigateTo('WebinarDetails', { params: { eventId } });
                  } else {
                    navigateTo('Agenda', {
                      query: { broadcastId: c?.broadcast_id }
                    });
                  }
                }
              : null
          });
        });
        break;
      default:
    }
  });

  const uniqueAlerts: FormattedAlert[] = [];

  formattedAlerts.forEach(alert => {
    if (!uniqueAlerts.find(a => a.message === alert.message)) {
      uniqueAlerts.push(alert);
    }
  });

  return uniqueAlerts;
}

export function addFormattedAlertToEntity(
  formattedAlertForEntityItems: FormattedAlertForEntityItems,
  entityId: string,
  formattedAlert: FormattedAlert
) {
  if (formattedAlertForEntityItems[entityId]) {
    formattedAlertForEntityItems[entityId].push(formattedAlert);
  } else {
    formattedAlertForEntityItems[entityId] = [formattedAlert];
  }
}

export function formatFailedChecksForEntityItems(failedChecks: FailedChecks | {} = {}): FormattedAlertForEntityItems {
  const formattedAlertForEntityItems: FormattedAlertForEntityItems = {};

  Object.keys(failedChecks).forEach(key => {
    const failedCheck = failedChecks[key];
    if (key === 'email_template_missing_qrcode_or_location') {
      (failedCheck as EmailTemplatePreEventCheck[]).forEach(c => {
        const formattedAlert: FormattedAlert = {
          key,
          message: `QR code or event location is missing in attached email template. Please review template`,
          actionRequired: !!c.action_required,
          action: isUserHavePermission(['email_template:update'])
            ? () =>
                navigateTo('EmailTemplates', {
                  query: { emailTemplateId: c.event_email_template || '' }
                })
            : null,
          entityId: c.event_email_template + c.email_type
        };
        if (c.event_email_template) {
          addFormattedAlertToEntity(
            formattedAlertForEntityItems,
            c.event_email_template + c.email_type,
            formattedAlert
          );
        }
      });
    }
  });

  return formattedAlertForEntityItems;
}

export function formatFailedChecksForAgendaItems(failedChecks: FailedChecks | {} = {}): FormattedAlertForAgendaItems {
  const formattedAlertsForAgendaItem: FormattedAlertForAgendaItems = {};
  const isEventRoleEnabled = store.state.org.currentOrg?.is_event_roles_enabled;
  const event = store.getters['studioEventList/getCurrentEvent'];
  const showStaffAlerts = isEventRoleEnabled && event?.host_type !== 'In_Person';

  Object.keys(failedChecks).forEach(key => {
    const failedCheck = failedChecks[key];

    switch (key as keyof FailedChecks) {
      case 'broadcast_has_no_speaker':
        (failedCheck as BroadcastCheck[]).forEach((c, i) => {
          const key = `broadcast_has_no_speaker_${c.broadcast_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key: key,
            message: 'Session has no speakers.',
            actionRequired: !!c.action_required
          };
          addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
        });
        break;
      case 'broadcast_has_no_coordinator':
        if (showStaffAlerts) {
          (failedCheck as BroadcastCheck[]).forEach((c, i) => {
            const key = `broadcast_has_no_coordinator_${c.broadcast_id}${i}`;
            const formattedAlert: FormattedAlert = {
              key: key,
              message: 'Session has no coordinator.',
              actionRequired: !!c.action_required
            };
            addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
          });
        }
        break;
      case 'broadcast_has_no_producer':
        if (showStaffAlerts) {
          (failedCheck as BroadcastCheck[]).forEach((c, i) => {
            const key = `broadcast_has_no_producer_${c.broadcast_id}${i}`;
            const formattedAlert: FormattedAlert = {
              key: key,
              message: 'Session has no producer.',
              actionRequired: !!c.action_required
            };
            addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
          });
        }
        break;
      case 'simulive_broadcast_have_improper_schedule':
        (failedCheck as BroadcastCheck[]).forEach((c, i) => {
          const key = `simulive_broadcast_have_improper_schedule${c.broadcast_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key,
            message: 'Session has an improper schedule.',
            actionRequired: !!c.action_required
          };
          addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
        });

        break;
      case 'simulive_broadcast_with_improper_video_length':
        (failedCheck as BroadcastVideoAssetPreEventCheck[]).forEach((c, i) => {
          const key = `simulive_broadcast_with_improper_video_length${c.broadcast_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key,
            actionRequired: !!c.action_required,
            message:
              {
                TOO_LONG:
                  'Goldcast recommends your session be 2 minutes longer than your video length, to ensure the video plays fully. Your video may be cut off at the end of your session time.',
                TOO_SHORT: 'Pre-recorded videos should be 2 min shorter than your session'
              }[c.error_type] ||
              `Total video length exception for your session "${c.broadcast_title} - ${c.error_type}"`,
            helpDoc:
              c.error_type === 'TOO_SHORT'
                ? '<a target="_blank" href="https://help.goldcast.io/program-agenda/15349971749403-pre-recorded-video-recommended-length">Learn more.</a>'
                : ''
          };
          addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
        });

        break;
      case 'simulive_broadcast_with_no_video':
        (failedCheck as BroadcastCheck[]).forEach((c, i) => {
          const key = `simulive_broadcast_with_no_video${c.broadcast_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key,
            message: 'Session has no video.',
            actionRequired: !!c.action_required
          };
          addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
        });
        break;
      case 'upload_slide_errors':
        (failedCheck as SlideAssetWithBroadcast[]).forEach((c, i) => {
          const hasMp4Error = c.transcoder_errors?.includes('SLIDE_CONTAINS_MP4');
          const slideConversionFailed = c.transcoder_errors?.includes('SLIDE_CONVERSION_FAILED');
          if (!hasMp4Error && !slideConversionFailed) {
            return;
          }
          const key = `upload_slide_errors_${c.broadcast_id}${i}`;
          const formattedAlert: FormattedAlert = {
            key,
            message: hasMp4Error
              ? `Animated slides aren't supported for decks that contain embedded video.`
              : `Animations couldn't be processed. Slides can still be shared, but without animations.`,
            actionRequired: !!c.action_required
          };
          addFormattedAlertToEntity(formattedAlertsForAgendaItem, c.broadcast_id, formattedAlert);
        });
        break;
      default:
    }
  });

  return formattedAlertsForAgendaItem;
}

export function formatFailedChecksForSeries(failedChecks: FailedSeriesChecks | {} = {}): FormattedAlert[] {
  const formattedAlerts: FormattedAlert[] = [];

  Object.keys(failedChecks).forEach(key => {
    const failedCheck = failedChecks[key];

    switch (key as keyof FailedSeriesChecks) {
      case 'series_email_template_missing_magic_links':
        (failedCheck as SeriesEmailTemplateCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `${c.email_type} email does not include users' entrance links`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['email_template:update'])
              ? () =>
                  navigateTo('SeriesEmailSchedule', {
                    query: { emailTemplateId: c.email_type || '' }
                  })
              : null
          });
        });
        break;
      case 'series_email_template_missing_model_row':
        (failedCheck as SeriesEmailTemplateCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `${c.email_type} email is missing an event list model row`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['email_template:update'])
              ? () =>
                  navigateTo('SeriesEmailSchedule', {
                    query: { emailTemplateId: c.email_type || '' }
                  })
              : null
          });
        });
        break;
      case 'series_email_template_multiple_model_row':
        (failedCheck as SeriesEmailTemplateCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: `${c.email_type} email has multiple event list model rows`,
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['email_template:update'])
              ? () =>
                  navigateTo('SeriesEmailSchedule', {
                    query: { emailTemplateId: c.email_type || '' }
                  })
              : null
          });
        });
        break;
      case 'series_registration_confirmation_email_turned_off':
        (failedCheck as SeriesEmailTemplateCheck[]).forEach(c => {
          formattedAlerts.push({
            key,
            message: 'Registration confirmation emails will not be sent',
            actionRequired: !!c.action_required,
            action: isUserHavePermission(['event:update'])
              ? () =>
                  navigateTo('SeriesEmailSchedule', {
                    query: { emailTemplateId: c.email_type || '' }
                  })
              : null
          });
        });
        break;
      default:
    }
  });

  const uniqueAlerts: FormattedAlert[] = [];

  formattedAlerts.forEach(alert => {
    if (!uniqueAlerts.find(a => a.message === alert.message)) {
      uniqueAlerts.push(alert);
    }
  });

  return uniqueAlerts;
}
